/**
 * Password Step Component
 * 
 * Handles password input for login flow with strength validation for signup.
 * Memoized for performance optimization.
 */

import { useState, memo } from "react";

import { Lock, Eye, EyeOff, Mail, KeyRound, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

import type { AuthState, AuthAction } from "@/types/state";

interface PasswordStepProps {
  state: AuthState;
  dispatch: (action: AuthAction) => void;
  onSubmit: () => void;
  onBack: () => void;
  onForgotPassword?: () => void;
  onMagicLink?: () => void;
  onPasskey?: () => void;
  passkeyLoading?: boolean;
  isSignup?: boolean;
}

const PasswordStepComponent = ({ 
  state, 
  dispatch, 
  onSubmit, 
  onBack,
  onForgotPassword,
  onMagicLink,
  onPasskey,
  passkeyLoading = false,
  isSignup = false 
}: PasswordStepProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    dispatch({ type: 'SET_PASSWORD', payload: password });

    if (isSignup) {
      // Calculate password strength
      let strength: 'weak' | 'medium' | 'strong' = 'weak';
      if (password.length >= 12 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) {
        strength = 'strong';
      } else if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
        strength = 'medium';
      }

      dispatch({
        type: 'SET_PASSWORD_VALIDATION',
        payload: {
          strength,
          error: password.length < 8 ? 'Password must be at least 8 characters' : null
        }
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch({ type: 'SET_PASSWORD_TOUCHED', payload: true });
    
    if (state.password && (!isSignup || state.password.length >= 8)) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={state.password}
            onChange={handlePasswordChange}
            onBlur={() => dispatch({ type: 'SET_PASSWORD_TOUCHED', payload: true })}
            disabled={state.loading || state.step === 'submitting'}
            className="pl-10 pr-10"
            autoComplete={isSignup ? "new-password" : "current-password"}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {state.passwordTouched && state.passwordError && (
          <p className="text-sm text-destructive">{state.passwordError}</p>
        )}
        {isSignup && state.passwordStrength && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  state.passwordStrength === 'weak' ? 'w-1/3 bg-destructive' :
                  state.passwordStrength === 'medium' ? 'w-2/3 bg-yellow-500' :
                  'w-full bg-green-500'
                }`}
              />
            </div>
            <span className="text-xs text-muted-foreground capitalize">
              {state.passwordStrength}
            </span>
          </div>
        )}
      </div>

      {!isSignup && onForgotPassword && (
        <div className="text-right">
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={onForgotPassword}
            disabled={state.loading}
          >
            Forgot password?
          </Button>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={state.loading || state.step === 'submitting'}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          type="submit"
          disabled={!state.password || state.loading || state.step === 'submitting'}
          className="flex-1"
        >
          {state.step === 'submitting' ? 'Signing in...' : isSignup ? 'Create Account' : 'Sign In'}
        </Button>
      </div>

      {/* Alternative authentication methods - only for login */}
      {!isSignup && (onMagicLink || onPasskey) && (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {onMagicLink && (
              <Button
                type="button"
                variant="outline"
                onClick={onMagicLink}
                disabled={state.loading || state.step === 'submitting'}
                className="w-full"
              >
                <Mail className="mr-2 h-4 w-4" />
                Email me a magic link
              </Button>
            )}

            {onPasskey && state.passkeySupported && !state.checkingPasskeySupport && (
              <Button
                type="button"
                variant="outline"
                onClick={onPasskey}
                disabled={state.loading || passkeyLoading || state.step === 'submitting'}
                className="w-full"
              >
                <KeyRound className="mr-2 h-4 w-4" />
                {passkeyLoading ? 'Authenticating...' : 'Sign in with passkey'}
              </Button>
            )}
          </div>

          <p className="text-xs text-center text-muted-foreground">
            <ShieldCheck className="inline h-3 w-3 mr-1" />
            Alternative authentication methods provide secure, password-free access
          </p>
        </>
      )}
    </form>
  );
};

export const PasswordStep = memo(PasswordStepComponent);
